/**
 * Water (03 §2.1, §5.1 step 4): the Radar mission's barrier across the belt - frozen river
 * with weak ice in the arctic, a river or wadi with fords elsewhere - and the ponds, icy
 * ponds and oases of the approaches.
 */

import { cos, HALF_PI, sin, TAU } from '../sim/math';
import { HAZARDS } from '../sim/tuning';
import type { WaterBarrierDef } from '../sim/types';
import { NO_BUILD, round2 } from './builder';
import { FLAG } from './grid';
import { RIVER } from './tuning';
import type { GenCtx } from './types';

/** z of a barrier's centre line at a given x (its course runs west to east). */
export function courseZ(course: WaterBarrierDef['course'], x: number): number {
  const pts = course.points;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    if (x >= a[0] && x <= b[0]) return b[0] === a[0] ? a[1] : a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0]);
  }
  const edge = x < (pts[0]?.[0] ?? 0) ? pts[0] : pts[pts.length - 1];
  return edge ? edge[1] : 0;
}

/** The barrier sits in the middle of the belt; the road crosses it at a ford, thick ice or a bridge. */
export function buildRiver(ctx: GenCtx): void {
  const rng = ctx.rng.fork('river');
  const belt = ctx.bands[ctx.bands.length - 2];
  const beltLine = ctx.plan.lines[ctx.plan.lines.length - 1];
  if (!belt || !beltLine) return;
  const width = Math.min(Math.round(rng.range(RIVER.width[0], RIVER.width[1])), belt.z1 - belt.z0 - 12);
  // North of centre: the belt's wire and the end of the last ravine need the south bank's room.
  const zc = (belt.z0 + belt.z1) / 2 - 3;
  const points: Array<[number, number]> = [];
  for (let x = -6; x <= ctx.sizeX + 6.01; x += (ctx.sizeX + 12) / 7) {
    points.push([round2(x), round2(zc + rng.range(-2.5, 2.5))]);
  }
  const course = { points, width };
  const crossings = [beltLine.roadX, beltLine.quietX];
  if (rng.chance(0.5)) crossings.push(beltLine.midX);

  const weakIce: WaterBarrierDef['weakIce'] = [];
  if (ctx.kit.water.surface === 'ice') {
    const wanted = Math.round(rng.range(RIVER.weakIce[0], RIVER.weakIce[1]));
    for (let tries = 0; tries < 60 && weakIce.length < wanted; tries++) {
      const r = Math.round(rng.range(RIVER.weakIceRadius[0], RIVER.weakIceRadius[1]) * 10) / 10;
      const x = Math.round(rng.range(4, ctx.sizeX - 4) * 10) / 10;
      // Weak ice never touches a crossing: those are the thick ice the player can rely on.
      if (crossings.some((c) => Math.abs(c - x) < HAZARDS.crossingHalfWidth + r + 1.5)) continue;
      if (weakIce.some((w) => Math.abs(w.x - x) < w.r + r)) continue;
      const z = courseZ(course, x) + rng.range(-1, 1) * Math.max(0, width / 2 - r);
      weakIce.push({ x, z: Math.round(z * 10) / 10, r });
    }
  }
  ctx.map.waterBarriers.push({ course, surface: ctx.kit.water.surface, weakIce, crossings });
  ctx.map.mask.line(points, width / 2 + 1.5, FLAG.water);
  for (const c of crossings) ctx.map.mask.circle(c, courseZ(course, c), width / 2 + 3, FLAG.keep);

  if (ctx.params.threat >= ctx.kit.water.bridgeFromThreat) {
    ctx.map.add('bridge', beltLine.roadX, courseZ(course, beltLine.roadX), { yaw: HALF_PI, length: width + 3 });
  }
}

/** A few ponds in the drop band and the approaches; in the desert they are oases ringed with palms. */
export function buildPonds(ctx: GenCtx): void {
  const rng = ctx.rng.fork('ponds');
  const [lo, hi] = ctx.kit.ponds;
  const wanted = lo + rng.int(hi - lo + 1);
  const south = ctx.bands[0];
  const belt = ctx.bands[ctx.bands.length - 2];
  if (!south || !belt) return;
  const avoid = NO_BUILD | FLAG.path;
  for (let tries = 0; tries < 40 && ctx.map.ponds.length < wanted; tries++) {
    const r = Math.round(rng.range(3, 6) * 10) / 10;
    const x = Math.round(rng.range(8 + r, ctx.sizeX - 8 - r));
    const z = Math.round(rng.range(belt.z1 + r + 4, south.z1 - r - 6));
    if (ctx.map.mask.hitsCircle(x, z, r + 2.5, avoid)) continue;
    ctx.map.ponds.push({ x, z, r });
    ctx.map.mask.circle(x, z, r + 1, FLAG.water);
    if (!ctx.kit.oasis) continue;
    const palms = 4 + rng.int(3);
    for (let p = 0; p < palms; p++) {
      const a = rng.range(0, TAU);
      const d = r + rng.range(1.5, 4);
      ctx.map.place('palm', x + sin(a) * d, z + cos(a) * d, { scale: rng.range(0.9, 1.2), yaw: a, margin: 0.4 });
    }
  }
}
